import React, { useState, useEffect, useCallback } from "react";
import { motion } from "framer-motion";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL
  ? `${process.env.REACT_APP_BACKEND_URL}/api`
  : "/api";

const STATUS_META = {
  active: { label: "Activo", cls: "bg-green-100 text-green-700", dot: "bg-green-500 animate-pulse" },
  disconnected: { label: "Desconectado", cls: "bg-red-100 text-red-700", dot: "bg-red-500" },
  never_connected: { label: "Esperando primera conexion", cls: "bg-amber-100 text-amber-700", dot: "bg-amber-400" },
  pending: { label: "Pendiente", cls: "bg-amber-100 text-amber-700", dot: "bg-amber-400" },
  unknown: { label: "Sin datos en el manager", cls: "bg-gray-100 text-gray-600", dot: "bg-gray-400" },
};

const fmt = (d) => {
  if (!d) return "—";
  try {
    return new Date(d).toLocaleString("es-AR", {
      day: "2-digit", month: "2-digit", year: "numeric",
      hour: "2-digit", minute: "2-digit", hour12: false,
    });
  } catch (e) { return String(d); }
};

const CodeBlock = ({ lines, testid }) => {
  const text = lines.join("\n");
  return (
    <div className="relative">
      <pre data-testid={testid}
        className="bg-gray-900 text-green-300 rounded-lg p-4 pr-24 text-xs font-mono overflow-x-auto whitespace-pre-wrap break-all">
        {text}
      </pre>
      <button data-testid={testid + "-copy"}
        onClick={() => navigator.clipboard.writeText(text)}
        className="absolute top-2 right-2 text-xs px-2 py-1 bg-white/10 hover:bg-white/20 text-white rounded transition">
        Copiar
      </button>
    </div>
  );
};

const installSnippets = (manager, agentName, key) => {
  const host = manager?.host || "siem.costillatech.com";
  const port = manager?.port || "1514";
  const ver = manager?.agent_version || "4.9.2";
  return {
    linux: [
      "# 1) Descargar el agente (Debian/Ubuntu; para RHEL usar el .rpm)",
      "curl -fL -o /tmp/wazuh-agent.deb https://packages.wazuh.com/4.x/apt/pool/main/w/wazuh-agent/wazuh-agent_" + ver + "-1_amd64.deb",
      "",
      "# 2) Instalar apuntando al manager de CostillaTech",
      "sudo WAZUH_MANAGER='" + host + "' WAZUH_MANAGER_PORT='" + port + "' WAZUH_PROTOCOL='tcp' \\",
      "     WAZUH_AGENT_NAME='" + agentName + "' apt-get install -y /tmp/wazuh-agent.deb",
      "",
      "# 3) Importar la clave de este agente",
      "printf 'y\\n' | sudo /var/ossec/bin/manage_agents -i '" + key + "'",
      "",
      "# 4) Verificar que apunte al manager (si ya dice " + host + ", saltear el sed)",
      "grep '<address>' /var/ossec/etc/ossec.conf",
      "sudo sed -i 's#<address>[^<]*</address>#<address>" + host + "</address>#' /var/ossec/etc/ossec.conf",
      "",
      "# 5) Arrancar",
      "sudo systemctl enable --now wazuh-agent",
      "sudo systemctl status wazuh-agent --no-pager",
    ],
    windows: [
      "# PowerShell COMO ADMINISTRADOR",
      "Invoke-WebRequest -UseBasicParsing -Uri https://packages.wazuh.com/4.x/windows/wazuh-agent-" + ver + "-1.msi -OutFile $env:TEMP\\wazuh-agent.msi",
      "",
      "msiexec.exe /i $env:TEMP\\wazuh-agent.msi /qn WAZUH_MANAGER='" + host + "' WAZUH_MANAGER_PORT='" + port + "' WAZUH_PROTOCOL='TCP' WAZUH_AGENT_NAME='" + agentName + "'",
      "",
      "$Key = '" + key + "'",
      "cmd.exe /c \"echo y| `\"C:\\Program Files (x86)\\ossec-agent\\manage_agents.exe`\" -i $Key\"",
      "",
      "Restart-Service WazuhSvc",
    ],
  };
};

const AgentKeyModal = ({ data, manager, isNew, onClose, onDownload }) => {
  const [os, setOs] = useState(data?.os_type === "windows" ? "windows" : "linux");
  if (!data) return null;
  const snips = installSnippets(manager, data.agent_name, data.key);
  return (
    <div style={{ zIndex: 10050 }} className="fixed inset-0 bg-black/60 flex items-center justify-center p-4 overflow-y-auto">
      <motion.div initial={{ opacity: 0, scale: 0.97 }} animate={{ opacity: 1, scale: 1 }}
        data-testid="wazuh-key-modal"
        className="bg-white rounded-2xl w-full max-w-3xl my-8 max-h-[90vh] overflow-y-auto">
        <div className="px-6 py-4 border-b border-gray-100 sticky top-0 bg-white rounded-t-2xl">
          <h3 className="text-lg font-bold text-gray-900">
            {isNew ? "Agente creado" : "Clave del agente"}: <span className="font-mono text-red-700">{data.agent_name}</span>
          </h3>
          <p className="text-sm text-gray-500 mt-0.5">ID en el manager: <span className="font-mono">{data.agent_id}</span></p>
        </div>

        <div className="p-6 space-y-5">
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3" data-testid="wazuh-key-warning">
            <p className="text-sm text-amber-900">
              Esta clave identifica al equipo dentro de <strong>su empresa</strong>. Copiela ahora:
              si la pierde puede volver a verla desde la lista de agentes, pero no la comparta por canales abiertos.
            </p>
          </div>

          <div>
            <p className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-2">Clave de registro</p>
            <CodeBlock testid="wazuh-agent-key" lines={[data.key]} />
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs font-semibold uppercase tracking-wider text-gray-500">Instalacion en el equipo del cliente</p>
              <div className="flex gap-1 bg-gray-100 rounded-lg p-1">
                {[{ id: "linux", label: "Linux" }, { id: "windows", label: "Windows" }].map((o) => (
                  <button key={o.id} data-testid={"wazuh-os-" + o.id} onClick={() => setOs(o.id)}
                    className={`px-3 py-1 text-xs font-medium rounded-md transition ${os === o.id ? "bg-white text-red-700 shadow-sm" : "text-gray-500 hover:text-gray-700"}`}>
                    {o.label}
                  </button>
                ))}
              </div>
            </div>
            <CodeBlock testid="wazuh-install-cmds" lines={snips[os]} />
            <div className="flex items-center gap-3 mt-3">
              <button data-testid="wazuh-download-installer" onClick={() => onDownload(data.agent_id, os)}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 transition">
                Descargar instalador ({os === "windows" ? ".ps1" : ".sh"})
              </button>
              <span className="text-xs text-gray-400">Script con la clave ya embebida: se corre con privilegios de administrador.</span>
            </div>
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-sm text-blue-900 space-y-1">
            <p className="font-medium">Requisito de red</p>
            <p className="text-xs text-blue-700">
              El equipo necesita <strong>salida TCP {manager?.port || "1514"}</strong> hacia{" "}
              <span className="font-mono">{manager?.host || "siem.costillatech.com"}</span>. No hace falta abrir ningun puerto
              entrante ni VPN. Si el cliente tiene firewall de egreso, hay que habilitar ese destino.
            </p>
          </div>
        </div>

        <div className="px-6 py-4 border-t border-gray-100 flex justify-end sticky bottom-0 bg-white rounded-b-2xl">
          <button data-testid="wazuh-key-modal-close" onClick={onClose}
            className="px-4 py-2 text-sm font-medium bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition">
            Cerrar
          </button>
        </div>
      </motion.div>
    </div>
  );
};

const WazuhAgentsPanel = ({ cParam = "", currentUser, onChanged }) => {
  const [agents, setAgents] = useState([]);
  const [manager, setManager] = useState(null);
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState("");
  const [osType, setOsType] = useState("linux");
  const [busy, setBusy] = useState(false);
  const [modal, setModal] = useState(null);
  const [isNew, setIsNew] = useState(false);
  const [error, setError] = useState("");

  const headers = () => {
    const token = localStorage.getItem("token");
    return token ? { Authorization: `Bearer ${token}` } : {};
  };
  const q = (sep) => (cParam ? sep + cParam : "");

  const fetchAgents = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/siem/agents/mine` + q("?"), { headers: headers() });
      setAgents(res.data.agents || []);
      setManager(res.data.manager || null);
    } catch (err) {
      setError(err.response?.data?.detail || "No se pudieron cargar los agentes");
    } finally { setLoading(false); }
  }, [cParam]);

  useEffect(() => { fetchAgents(); }, [fetchAgents]);

  const register = async () => {
    if (!name.trim()) return;
    setBusy(true); setError("");
    try {
      const res = await axios.post(`${API}/siem/agents/register` + q("?"),
        { name: name.trim(), os_type: osType }, { headers: headers() });
      setModal({ ...res.data, os_type: osType });
      setIsNew(true);
      setName("");
      await fetchAgents();
      if (onChanged) onChanged();
    } catch (err) {
      setError(err.response?.data?.detail || "No se pudo crear el agente");
    } finally { setBusy(false); }
  };

  const showKey = async (agent) => {
    setError("");
    try {
      const res = await axios.get(`${API}/siem/agents/${agent.agent_id}/key` + q("?"), { headers: headers() });
      setModal({ ...res.data, os_type: agent.os_type });
      setIsNew(false);
    } catch (err) {
      setError(err.response?.data?.detail || "No se pudo obtener la clave");
    }
  };

  const removeAgent = async (agent) => {
    if (!window.confirm(`Dar de baja el agente "${agent.agent_name}"? Deja de enviar telemetria.`)) return;
    setError("");
    try {
      await axios.delete(`${API}/siem/agents/${agent.agent_id}` + q("?"), { headers: headers() });
      await fetchAgents();
      if (onChanged) onChanged();
    } catch (err) {
      setError(err.response?.data?.detail || "No se pudo dar de baja el agente");
    }
  };

  const downloadInstaller = async (agentId, os) => {
    try {
      const sep = cParam ? "&" : "";
      const res = await axios.get(
        `${API}/siem/agents/${agentId}/installer?os_kind=${os}` + (cParam ? sep + cParam : ""),
        { headers: headers(), responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = os === "windows" ? "instalar-agente-wazuh.ps1" : "instalar-agente-wazuh.sh";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError("No se pudo descargar el instalador");
    }
  };

  // Vista consolidada del superadmin: no hay empresa elegida, asi que no se puede
  // dar de alta un agente sin que caiga en la empresa equivocada.
  const consolidated = currentUser?.role === "superadmin" && !cParam;
  const canManage = (currentUser?.role === "admin" || currentUser?.role === "superadmin") && !consolidated;

  return (
    <motion.div key="agents" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
      data-testid="wazuh-agents-panel" className="space-y-6">

      <div className="bg-gradient-to-r from-slate-800 to-slate-900 rounded-2xl p-5 text-white">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h3 className="text-lg font-bold">Agentes de telemetria interna</h3>
            <p className="text-slate-300 text-sm mt-0.5 max-w-2xl">
              El escaneo de CostillaTech mira desde afuera. El agente se instala dentro del servidor del
              cliente y reporta lo que pasa adentro: accesos, cambios de archivos, malware, integridad
              y intentos de explotacion en vivo.
            </p>
          </div>
          <div className="text-xs text-slate-300 bg-white/5 rounded-lg px-3 py-2" data-testid="wazuh-manager-info">
            <p>Manager: <span className="font-mono text-white">{manager?.host || "—"}:{manager?.port || "—"}</span></p>
            <p className="mt-0.5">Version del agente: <span className="font-mono text-white">{manager?.agent_version || "—"}</span></p>
          </div>
        </div>
      </div>

      {error && (
        <div data-testid="wazuh-agents-error" className="bg-red-50 border border-red-200 text-red-800 text-sm rounded-lg p-3">
          {error}
        </div>
      )}

      {consolidated && (
        <div data-testid="wazuh-agents-pick-company"
          className="bg-amber-50 border border-amber-200 rounded-2xl p-4 text-sm text-amber-900">
          Esta es la vista consolidada. Elegi una empresa en el selector de arriba para ver
          sus agentes o registrar uno nuevo.
        </div>
      )}

      {canManage && (
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
          <h4 className="font-semibold text-gray-800 mb-1">Registrar un equipo</h4>
          <p className="text-sm text-gray-500 mb-4">
            Un agente por servidor. Pongale un nombre reconocible (ej.{" "}
            <span className="font-mono">servidor-facturacion</span>) y elija su sistema operativo.
            CostillaTech genera la clave y el instalador; el equipo solo necesita salida TCP {manager?.port || "1514"}.
          </p>
          <div className="flex flex-wrap gap-3 items-end">
            <div className="flex-1 min-w-[220px]">
              <label className="block text-xs font-medium text-gray-500 mb-1">Nombre del equipo</label>
              <input data-testid="wazuh-agent-name-input" value={name} onChange={(e) => setName(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") register(); }}
                placeholder="servidor-facturacion"
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-red-500 focus:border-transparent" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Sistema operativo</label>
              <select data-testid="wazuh-agent-os-select" value={osType} onChange={(e) => setOsType(e.target.value)}
                className="px-3 py-2 border border-gray-200 rounded-lg text-sm bg-white focus:ring-2 focus:ring-red-500">
                <option value="linux">Linux</option>
                <option value="windows">Windows</option>
              </select>
            </div>
            <button data-testid="wazuh-register-agent-btn" onClick={register} disabled={busy || !name.trim()}
              className="px-5 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 transition disabled:opacity-50">
              {busy ? "Creando..." : "Generar agente + clave"}
            </button>
          </div>
        </div>
      )}

      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
        <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
          <h4 className="font-semibold text-gray-800">Agentes registrados ({agents.length})</h4>
          <button data-testid="wazuh-refresh-agents" onClick={fetchAgents}
            className="text-xs px-3 py-1.5 bg-gray-100 text-gray-600 rounded-lg hover:bg-gray-200 font-medium transition">
            {loading ? "Cargando..." : "Actualizar"}
          </button>
        </div>

        {agents.length === 0 ? (
          <div className="text-center py-12 px-6" data-testid="wazuh-agents-empty">
            <p className="text-gray-500 font-medium">Todavia no hay agentes instalados</p>
            <p className="text-gray-400 text-sm mt-1 max-w-lg mx-auto">
              Sin agente, el panel SIEM muestra solo el monitoreo perimetral de CostillaTech. Registre el
              primer equipo arriba para empezar a recibir telemetria interna.
            </p>
          </div>
        ) : (
          <div className="divide-y divide-gray-50">
            {agents.map((a) => {
              const meta = STATUS_META[a.status] || STATUS_META.unknown;
              return (
                <div key={a.agent_id} data-testid={`wazuh-agent-${a.agent_id}`}
                  className="px-5 py-4 flex items-center gap-4 flex-wrap">
                  <span className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${meta.dot}`}></span>
                  <div className="min-w-[200px] flex-1">
                    <p className="font-medium text-gray-800 text-sm">{a.label || a.agent_name}</p>
                    <p className="text-xs text-gray-400 font-mono">{a.agent_name} &middot; ID {a.agent_id}</p>
                  </div>
                  <div className="text-xs text-gray-500 min-w-[170px]">
                    <p>Ultimo reporte: {fmt(a.last_keepalive)}</p>
                    <p className="text-gray-400">{a.os_name || (a.os_type === "windows" ? "Windows" : "Linux")}{a.agent_version ? " · " + a.agent_version : ""}</p>
                  </div>
                  <span data-testid={`wazuh-agent-status-${a.agent_id}`}
                    className={`text-xs font-medium px-2.5 py-1 rounded-full ${meta.cls}`}>
                    {meta.label}
                  </span>
                  {canManage && (
                    <div className="flex gap-2">
                      <button data-testid={`wazuh-agent-key-${a.agent_id}`} onClick={() => showKey(a)}
                        className="text-xs px-3 py-1.5 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 font-medium transition">
                        Ver clave / instalador
                      </button>
                      <button data-testid={`wazuh-agent-delete-${a.agent_id}`} onClick={() => removeAgent(a)}
                        className="text-xs px-3 py-1.5 bg-red-50 text-red-700 rounded-lg hover:bg-red-100 font-medium transition">
                        Dar de baja
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5" data-testid="wazuh-agents-help">
        <h4 className="font-semibold text-gray-800 mb-3">Como funciona</h4>
        <ol className="text-sm text-gray-600 space-y-2 list-decimal list-inside">
          <li>Registra el equipo aca y CostillaTech crea la identidad del agente con su clave.
            <strong> Un agente por servidor</strong>: cada equipo se registra por separado y reporta
            con su propio nombre.</li>
          <li>Descarga el instalador (o copia los comandos) y lo corre en el equipo como administrador.
            Hay instalador para <strong>Linux</strong> (Debian/Ubuntu y RHEL/Rocky/Alma) y para
            <strong>Windows Server</strong> 2016/2019/2022 y Windows 10/11 (paquete MSI + PowerShell).</li>
          <li>El agente abre una conexion <strong>saliente</strong> TCP {manager?.port || "1514"} hacia{" "}
            <span className="font-mono">{manager?.host || "siem.costillatech.com"}</span>. No se abren puertos entrantes.</li>
          <li>En 1 o 2 minutos pasa a <strong>Activo</strong> y sus eventos aparecen en la pestana Alertas
            como <span className="font-mono text-xs">Evento Agente</span>, solo dentro de esta empresa.</li>
        </ol>
      </div>

      {modal && (
        <AgentKeyModal data={modal} manager={manager} isNew={isNew}
          onClose={() => setModal(null)} onDownload={downloadInstaller} />
      )}
    </motion.div>
  );
};

export default WazuhAgentsPanel;
